import { ReactNode } from 'react';
import {
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Typography,
} from '@mui/material';
import { TOrderBy } from 'redux/types';
import { ESortDirection } from 'ui/EasTable/types';
import { humanize, toneOf } from './tableHelpers';

export type TPgMiniColumn<T> = {
	header: string;
	render: (row: T) => ReactNode;
	align?: 'left' | 'right' | 'center';
	/** Field of the gateway list the column sorts by (only with `onSortChange`). */
	sortField?: string;
};

type TPgMiniTableProps<T> = {
	columns: TPgMiniColumn<T>[];
	rows: T[];
	getKey: (row: T) => string | number;
	emptyText?: string;
	onRowClick?: (row: T) => void;
	/** Current server-side order; columns with `sortField` get a sort label. */
	sort?: TOrderBy | null;
	/** A click on a sortable header: a new column starts descending, the same one flips. */
	onSortChange?: (sort: TOrderBy) => void;
	/** `none` lets the table grow with its rows (when it is the main content). */
	maxHeight?: number | 'none';
};

const nextSort = (sort: TOrderBy | null | undefined, fieldName: string): TOrderBy => ({
	fieldName,
	direction:
		sort?.fieldName === fieldName && sort.direction === ESortDirection.DESC
			? ESortDirection.ASC
			: ESortDirection.DESC,
});

/** Small read-only table for blocks inside cards and dialogs. */
export const PgMiniTable = <T,>({
	columns,
	rows,
	getKey,
	emptyText = 'Nothing to show',
	onRowClick,
	sort,
	onSortChange,
	maxHeight = 360,
}: TPgMiniTableProps<T>) => {
	if (!rows.length) {
		return (
			<Typography variant="body2" color="text.secondary">
				{emptyText}
			</Typography>
		);
	}
	return (
		<TableContainer sx={{ maxHeight }}>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow>
						{columns.map(({ header, align, sortField }) => {
							const active = !!sortField && sort?.fieldName === sortField;
							return (
								<TableCell
									key={header}
									align={align}
									sx={{ fontWeight: 'bold' }}
									sortDirection={active && sort ? sort.direction : false}
								>
									{sortField && onSortChange ? (
										<TableSortLabel
											active={active}
											direction={active && sort ? sort.direction : ESortDirection.DESC}
											onClick={() => onSortChange(nextSort(sort, sortField))}
										>
											{header}
										</TableSortLabel>
									) : (
										header
									)}
								</TableCell>
							);
						})}
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row) => (
						<TableRow
							key={getKey(row)}
							hover={!!onRowClick}
							onClick={onRowClick ? () => onRowClick(row) : undefined}
							sx={onRowClick ? { cursor: 'pointer' } : undefined}
						>
							{columns.map((column) => (
								<TableCell key={column.header} align={column.align} sx={{ verticalAlign: 'top' }}>
									{column.render(row)}
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

const CHIP_COLOR = {
	success: 'success',
	error: 'error',
	warning: 'warning',
	info: 'info',
	default: 'default',
} as const;

type TPgStatusChipProps = { value: string | null | undefined; label?: ReactNode };

/** Status as a coloured chip. */
export const PgStatusChip = ({ value, label }: TPgStatusChipProps) =>
	value ? (
		<Chip
			size="small"
			label={label ?? humanize(value)}
			color={CHIP_COLOR[toneOf(value)]}
			sx={{ height: 20 }}
		/>
	) : (
		<span>—</span>
	);
